import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { TERRITOIRES, communesCourt, etatRegle, liste, majuscule, phrasePublic } from "./reglesSousOrientation";

// ============================================================
// Les règles d'une version en schéma (CE-1419, maquette 11), à la façon d'un
// flux : « Orienté vers la Caf », puis les règles reliées par « sinon », vers
// les organismes qu'elles alimentent. Les nœuds sont du HTML en grille ;
// une fois la mise en page faite, chaque organisme se place à la hauteur
// moyenne des règles qui l'alimentent, puis les liaisons se tracent en SVG.
// Survol ou focus d'un nœud : ses liaisons ressortent, le reste s'efface.
// Sous 992 px, une seule colonne, sans liaisons : chaque règle dit vers qui.
// ============================================================

const ECART_ORGANISMES = 16;

const centre = (el, base) => {
  const b = el.getBoundingClientRect();
  return { x: b.left - base.left, y: b.top - base.top + b.height / 2, droite: b.right - base.left };
};

/** Les organismes que les règles alimentent, dans l'ordre d'apparition, avec leurs mesures par territoire. */
function organismes(regles) {
  const parId = new Map();
  regles.forEach((regle) => {
    regle.operateurs.forEach((operateur) => {
      const organisme = parId.get(operateur.id) || { id: operateur.id, nom: operateur.nom, sources: [], mesures: new Map() };
      organisme.sources.push(regle.id);
      regle.territoires.forEach((t) => {
        const d = t.disponibilites.find((x) => x.operateurId === operateur.id);
        if (d) organisme.mesures.set(t.territoire, d);
      });
      parId.set(operateur.id, organisme);
    });
  });
  return [...parId.values()];
}

const SchemaRegles = ({ regles, annee, peutModifier, onModifier }) => {
  const refSchema = useRef(null);
  const [traces, setTraces] = useState({ largeur: 0, hauteur: 0, chemins: [] });
  const [actif, setActif] = useState(null);

  const vers = useMemo(() => organismes(regles), [regles]);
  const liens = useMemo(
    () => [
      { de: "entree", vers: regles[0].id, ton: "", etiquette: "" },
      ...regles.flatMap((regle) => {
        const etat = etatRegle(regle, annee);
        const ton = etat.ton !== "ok" ? etat.ton : "";
        return regle.operateurs.map((o, k) => ({
          de: regle.id,
          vers: `op-${o.id}`,
          ton,
          etiquette: k === 0 ? etat.court || (regle.operateurs.length > 1 ? "à parts égales" : "") : "",
        }));
      }),
      { de: "fin", vers: "caf", ton: "", etiquette: "" },
    ],
    [regles, annee]
  );

  useLayoutEffect(() => {
    const dessiner = () => {
      const schema = refSchema.current;
      if (!schema) return;
      const colonne = schema.querySelector(".schema__col--vers");
      const noeuds = [...colonne.querySelectorAll(".noeud--vers")];
      if (window.getComputedStyle(colonne).display === "none") {
        colonne.style.height = "";
        noeuds.forEach((n) => (n.style.top = ""));
        setTraces({ largeur: 0, hauteur: 0, chemins: [] });
        return;
      }
      const noeud = (cle) => schema.querySelector(`[data-noeud="${cle}"]`);
      const baseColonne = colonne.getBoundingClientRect();
      let bas = 0;
      noeuds
        .map((n) => {
          const ys = n.dataset.sources.split(" ").map((id) => centre(noeud(id), baseColonne).y);
          return { n, y: ys.reduce((a, b) => a + b, 0) / ys.length };
        })
        .sort((a, b) => a.y - b.y)
        .forEach(({ n, y }) => {
          const haut = Math.max(y - n.offsetHeight / 2, bas ? bas + ECART_ORGANISMES : 0);
          n.style.top = `${haut}px`;
          bas = haut + n.offsetHeight;
        });
      colonne.style.height = `${bas}px`;

      const base = schema.getBoundingClientRect();
      setTraces({
        largeur: base.width,
        hauteur: base.height,
        chemins: liens.map((lien) => {
          const a = centre(noeud(lien.de), base);
          const b = centre(noeud(lien.vers), base);
          const dx = Math.max(32, (b.x - a.droite) / 2);
          return { ...lien, x1: a.droite, y1: a.y, x2: b.x, y2: b.y, d: `M${a.droite} ${a.y} C${a.droite + dx} ${a.y} ${b.x - dx} ${b.y} ${b.x} ${b.y}` };
        }),
      });
    };
    dessiner();
    window.addEventListener("resize", dessiner);
    return () => window.removeEventListener("resize", dessiner);
  }, [liens]);

  const lies = useMemo(() => {
    if (!actif) return null;
    const cles = new Set([actif]);
    liens.forEach((l) => {
      if (l.de === actif || l.vers === actif) {
        cles.add(l.de);
        cles.add(l.vers);
      }
    });
    return cles;
  }, [actif, liens]);
  const classeNoeud = (cle) => (lies && lies.has(cle) ? " actif" : "");
  const lienActif = (l) => lies && (l.de === actif || l.vers === actif);
  const survol = (cle) => ({
    onMouseEnter: () => setActif(cle),
    onMouseLeave: () => setActif(null),
    onFocus: () => setActif(cle),
    onBlur: () => setActif(null),
  });

  return (
    <div className="schema-cadre">
      <div className={`schema${actif ? " schema--focus" : ""}`} ref={refSchema}>
        <svg className="schema__liens" aria-hidden="true" focusable="false" viewBox={`0 0 ${traces.largeur} ${traces.hauteur}`}>
          {traces.chemins.map((c) => {
            const classe = `${c.ton ? ` lien--${c.ton}` : ""}${lienActif(c) ? " actif" : ""}`;
            return (
              <g key={`${c.de}-${c.vers}`}>
                <path className={`lien${classe}`} d={c.d} />
                <circle className={`port${c.ton ? ` port--${c.ton}` : ""}${lienActif(c) ? " actif" : ""}`} cx={c.x1} cy={c.y1} r="4" />
                <circle className={`port${c.ton ? ` port--${c.ton}` : ""}${lienActif(c) ? " actif" : ""}`} cx={c.x2} cy={c.y2} r="4" />
                {c.etiquette && (
                  <text className={`lien__etiquette${c.ton ? ` lien__etiquette--${c.ton}` : ""}${lienActif(c) ? " actif" : ""}`} x={c.x1 + 10} y={c.y1 - 8}>
                    {c.etiquette}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        <div className="schema__col schema__col--entree">
          <div className={`noeud noeud--entree${classeNoeud("entree")}`} data-noeud="entree">
            <span className="noeud__titre">Orienté vers la Caf</span>
            {/* Toute orientation vers la Caf déclenche, automatique ou manuelle (CE-1370, 03/11/2026). */}
            <span className="noeud__sous">par l'algorithme ou par un agent</span>
          </div>
        </div>

        <ol className="schema__col schema__col--regles" aria-label={`Règles, examinées dans l'ordre`}>
          {regles.map((regle, i) => {
            const etat = etatRegle(regle, annee);
            const ton = etat.ton !== "ok" ? ` noeud--${etat.ton}` : "";
            const noms = liste(regle.operateurs.map((o) => o.nom), "et");
            const versTexte = `${regle.operateurs.length > 1 ? `Répartis à parts égales entre ${noms}` : `Vers ${noms}`}${etat.court ? `, ${etat.court}` : ""}`;
            const contenu = (
              <>
                <span className="noeud__tete">
                  <span className="noeud__rang">{regle.rang}</span>
                  <span className="noeud__titre">{majuscule(phrasePublic(regle.criteres))}</span>
                </span>
                <span className="noeud__sous">{communesCourt(regle.communes)}</span>
                <span className="noeud__vers">{versTexte}</span>
              </>
            );
            return (
              <li key={regle.id} className="schema__etape">
                <span className={`schema__sinon${i ? "" : " schema__sinon--debut"}`} aria-hidden="true">
                  {i ? "sinon" : ""}
                </span>
                {peutModifier ? (
                  <button
                    type="button"
                    className={`noeud noeud--regle${ton}${classeNoeud(regle.id)}`}
                    data-noeud={regle.id}
                    aria-label={`Modifier la règle ${regle.rang} : ${majuscule(phrasePublic(regle.criteres))}. ${versTexte}.`}
                    onClick={() => onModifier(regle)}
                    {...survol(regle.id)}
                  >
                    {contenu}
                  </button>
                ) : (
                  <div className={`noeud noeud--regle${ton}${classeNoeud(regle.id)}`} data-noeud={regle.id} {...survol(regle.id)}>
                    {contenu}
                  </div>
                )}
              </li>
            );
          })}
          <li className="schema__etape">
            <span className="schema__sinon" aria-hidden="true">
              sinon
            </span>
            <div className={`noeud noeud--fin${classeNoeud("fin")}`} data-noeud="fin">
              <span className="noeud__titre">Aucune règle ne s'applique</span>
              <span className="noeud__vers">Reste à la Caf, avec un travailleur social</span>
            </div>
          </li>
        </ol>

        <div className="schema__col schema__col--vers">
          {vers.map((organisme) => (
            <div
              key={organisme.id}
              className={`noeud noeud--vers${classeNoeud(`op-${organisme.id}`)}`}
              data-noeud={`op-${organisme.id}`}
              data-sources={organisme.sources.join(" ")}
              {...survol(`op-${organisme.id}`)}
            >
              <span className="noeud__titre">{organisme.nom}</span>
              <span className="noeud__sous">FSE+ {annee}</span>
              <span className="noeud__quotas">
                {TERRITOIRES.filter((t) => organisme.mesures.has(t.id)).map((t, i) => {
                  const d = organisme.mesures.get(t.id);
                  // Le séparateur hors du segment insécable : la ligne se coupe entre deux territoires.
                  return (
                    <React.Fragment key={t.id}>
                      {i > 0 && " · "}
                      <span className="insecable">
                        {t.libelle} {d.quota === null ? "sans quota" : <><b>{d.utilisees}</b> / {d.quota}</>}
                      </span>
                    </React.Fragment>
                  );
                })}
              </span>
            </div>
          ))}
          <div className={`noeud noeud--vers noeud--caf${classeNoeud("caf")}`} data-noeud="caf" data-sources="fin">
            <span className="noeud__titre">Caf</span>
            <span className="noeud__sous">Avec un travailleur social de la Caf</span>
          </div>
        </div>
      </div>
      <p className="schema__legende">
        <i className="fa fa-info-circle" aria-hidden="true" />
        <span>
          Les règles se lisent de haut en bas : si une règle concerne l'allocataire et que le quota n'est pas atteint, il part vers l'organisme relié ; sinon, on
          passe à la suivante.<span className="schema__survol"> Survolez une règle ou un organisme pour voir ses liaisons.</span>
        </span>
      </p>
    </div>
  );
};

export default SchemaRegles;
